<script setup lang="ts">
import { ChevronRightIcon } from '@blpde/nx-icons';
import { computed } from 'vue';
import { opensInNewTab } from './link';
import type { NavBarNode } from './types';

const props = defineProps<{
  node: NavBarNode;
  highlighted: boolean;
  open: boolean;
}>();

const emit = defineEmits<{
  activate: [event: PointerEvent | KeyboardEvent];
  pointerEnter: [];
}>();

const hasChildren = computed(() => props.node.children.length > 0);

/** A leaf with a target is a real link, so the browser opens it in a new tab. */
const linkHref = computed(() =>
  !hasChildren.value && !props.node.disabled ? props.node.href : undefined
);

function onClick(event: MouseEvent): void {
  // Middle / Ctrl- / Cmd-click on a link: the browser's new tab, not ours.
  if (linkHref.value && opensInNewTab(event)) return;
  if (linkHref.value) event.preventDefault();
  emit('activate', event as PointerEvent);
}
</script>

<template>
  <component
    :is="linkHref ? 'a' : 'button'"
    :type="linkHref ? undefined : 'button'"
    :href="linkHref"
    role="menuitem"
    class="menubar-item"
    :disabled="linkHref ? undefined : node.disabled || undefined"
    :aria-disabled="node.disabled || undefined"
    :data-state="open ? 'open' : undefined"
    :data-highlighted="highlighted ? '' : undefined"
    :data-id="node.id"
    :tabindex="node.disabled ? -1 : highlighted ? 0 : -1"
    :aria-haspopup="hasChildren ? 'menu' : undefined"
    :aria-expanded="hasChildren ? open : undefined"
    :aria-label="node.iconOnly ? node.text : undefined"
    @click="onClick"
    @pointerenter="emit('pointerEnter')"
  >
    <span v-if="node.icon" class="menubar-item-icon">
      <component :is="node.icon" filled class="nxt-icon-lg" />
    </span>
    <span v-if="!node.iconOnly">{{ node.text }}</span>
    <span v-if="hasChildren" class="right-slot">
      <ChevronRightIcon />
    </span>
  </component>
</template>

<style scoped>
@import url('./style.css');

.menubar-item-icon {
  display: inline-flex;
  margin-right: var(--nxt-space-xs);
}

.menubar-item:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}
</style>
